import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, Loader2, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { FieldEditor } from './FieldEditor';
import { useContent } from '../lib/ContentContext';

const SECTIONS = [
  { key: 'site', label: 'Site Logo & Branding' },
  { key: 'hero_slides', label: 'Homepage – Hero Slider' },
  { key: 'home_intro', label: 'Homepage – Intro & Certifications' },
  { key: 'home_industries', label: 'Homepage – Industries We Serve' },
  { key: 'home_about', label: 'Homepage – About Intexso' },
  { key: 'home_why', label: 'Homepage – Why Choose Intexso' },
  { key: 'home_solutions', label: 'Homepage – Solutions' },
  { key: 'home_news_header', label: 'Homepage – News Section Header' },
  { key: 'home_contact', label: 'Homepage – Contact Section' },
  { key: 'about_page', label: 'About Us Page' },
  { key: 'textile_page', label: 'Textile Chemicals Page' },
  { key: 'textile_products', label: 'Textile Product Tables' },
  { key: 'leather_page', label: 'Leather Chemicals Page' },
  { key: 'paper_page', label: 'Paper Chemicals Page' },
  { key: 'paints_page', label: 'Paints & Coatings Page' },
  { key: 'rd_page', label: 'R&D & Sustainability Page' },
  { key: 'news_page', label: 'News & Insights Page Header' },
  { key: 'contact_page', label: 'Contact Us Page' },
  { key: 'privacy_page', label: 'Privacy Policy Page' },
  { key: 'terms_page', label: 'Terms of Use Page' },
  { key: 'footer', label: 'Footer' },
];

export default function ContentEditor() {
  const { sectionKey } = useParams();
  const navigate = useNavigate();
  const { refresh } = useContent();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!sectionKey) {
      setData(null);
      return;
    }
    setLoading(true);
    api
      .get(`/content/${sectionKey}`)
      .then((res) => setData(res.data))
      .catch(() => toast.error('Failed to load section'))
      .finally(() => setLoading(false));
  }, [sectionKey]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/content/${sectionKey}`, { data });
      toast.success('Content saved & published');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!sectionKey) {
    return (
      <div data-testid="admin-content-list">
        <h1 className="text-2xl font-extrabold text-[#26247B]">Site Content</h1>
        <p className="mt-1 text-sm text-black/60">Select a section to edit its text, images and videos. Changes go live immediately after saving.</p>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              data-testid={`admin-content-section-${s.key}`}
              onClick={() => navigate(`/admin/content/${s.key}`)}
              className="flex items-center justify-between bg-white border border-[#E6E8EE] rounded-xl px-5 py-4 text-left text-sm font-bold text-[#26247B] hover:border-[#26247B] transition-colors duration-200"
            >
              {s.label} <ChevronRight className="h-4 w-4 shrink-0" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  const section = SECTIONS.find((s) => s.key === sectionKey);

  return (
    <div data-testid="admin-content-editor">
      <div className="flex items-center justify-between gap-4 sticky top-0 bg-[#F6F7F9] py-3 z-10">
        <div>
          <button onClick={() => navigate('/admin/content')} className="text-xs font-bold text-[#2E8B57] hover:underline">
            ← All sections
          </button>
          <h1 className="text-xl font-extrabold text-[#26247B] mt-1">{section?.label || sectionKey}</h1>
        </div>
        <button
          onClick={save}
          data-testid="admin-editor-save-button"
          disabled={saving || loading || !data}
          className="inline-flex items-center gap-2 rounded-lg bg-[#2E8B57] text-white px-6 py-2.5 text-sm font-bold transition-colors duration-200 hover:bg-[#257247] disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save & Publish
        </button>
      </div>

      <div className="mt-4 bg-white border border-[#E6E8EE] rounded-2xl p-6">
        {loading && (
          <div className="py-16 flex justify-center">
            <Loader2 className="h-7 w-7 animate-spin text-[#26247B]" />
          </div>
        )}
        {!loading && data && <FieldEditor fieldKey={sectionKey} value={data} onChange={setData} />}
      </div>
    </div>
  );
}
